import PropTypes from "prop-types";
import { Select, Table } from "antd";
import { ModalBody, ModalFooter, ModalTitle } from "@vds/modals";
import { Body, Micro, Title } from "@vds/typography";
import {
  fetchDeviceProfilesRequest,
  updateUI,
} from "../../../../store/actions/asset";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { ErrorMessage } from "../../../../global/components/ValidationIcon";
import ATSToolTip from "../../../../global/components/ATSToolTip";
import CircleIIcon from "../../../../assets/circle_i.svg";

dayjs.extend(relativeTime);

function CreateAssetStep2(props) {
  const { formData, onExit } = props;

  const isLoadingDeviceProfile = useSelector(
    (state) => state.asset.isLoading.deviceProfiles
  );

  const dispatch = useDispatch();
  const handleNext = () => {
    let errors = {};
    if (!formData?.step2?.deviceType) {
      errors.deviceType = true;
    }

    if (!formData?.step2?.deviceId?.id) {
      errors.deviceId = true;
    }

    if (!formData?.step2?.deviceProfile?.id) {
      errors.deviceProfile = true;
    }

    if (Object.keys(errors).length > 0) {
      dispatch(
        updateUI({
          formData: { ...formData, step2: { ...formData.step2, errors } },
        })
      );
      return;
    }

    dispatch(updateUI({ formData: { ...formData, selectedStep: 3 } }));
  };

  const handleFieldChange = (name, value, bleserial = null) => {
    let error = {};
    let deviceProfile = Array.isArray(formData?.step2?.deviceProfiles)
      ? formData?.step2?.deviceProfiles
      : [];

    if (name in formData?.step2?.errors) {
      error[name] = false;
    }

    if (name === "deviceProfile") {
      const attributes = deviceProfile
        ?.filter((profile) => profile?.profileid === value?.id)
        ?.flatMap((profile) => profile?.attributes || [])
        ?.map((attribute) => ({
          key: attribute.profileid,
          attribute: `${attribute.name}(${attribute.unit})`,
          minimum: attribute.min,
          maximum: attribute.max,
          alertType: attribute.alertType || "N/A",
          alertGroup: attribute.alertGroup?.join(", ") || "N/A",
        }));

      let locations = deviceProfile
        ?.filter((profile) => profile?.profileid === value?.id)
        ?.flatMap((profile) => profile?.locations || []);

      if (Array.isArray(locations) && locations.length > 0) {
        if (
          Array.isArray(locations[0]?.locationZone) &&
          locations[0]?.locationZone?.length > 0
        ) {
          locations = locations[0]?.locationZone?.map((loc, index) => ({
            key: `loc-${index}`,
            zone: loc,
            trigger: locations[0]?.trigger || "N/A",
            alerttype: locations[0]?.alerttype || "N/A",
            alertgroup: Array.isArray(locations[0]?.alertgroup)
              ? locations[0].alertgroup?.join(", ")
              : "N/A",
          }));
        }
      }

      const frequencies = deviceProfile
        ?.filter((profile) => profile?.profileid === value?.id)
        ?.map((profile) => profile?.frequencies || {});

      dispatch(
        updateUI({
          formData: {
            ...formData,
            step2: {
              ...formData.step2,
              [name]: value,
              errors: { ...formData.step2.errors, ...error },
              attributes,
              locations,
              frequencies: frequencies?.length > 0 ? frequencies[0] : {},
            },
          },
        })
      );
      return;
    }

    let bleSerialObj = {};
    if (bleserial) {
      bleSerialObj = { bleserial };
    }

    dispatch(
      updateUI({
        formData: {
          ...formData,
          step2: {
            ...formData.step2,
            deviceId: { id: "", name: "Select device name" },
            deviceProfile: { id: "", name: "Select device profile" },
            ...bleSerialObj,
            [name]: value,
            errors: { ...formData.step2.errors, ...error },
          },
        },
      })
    );
  };

  return (
    <>
      <ModalTitle>
        <Body size="large">Add asset</Body>
        <div className="mt-2">
          <Title size="large" bold={true}>
            Select device
          </Title>
        </div>
      </ModalTitle>
      <ModalBody>
        <div className="d-flex flex-column justify-content-start align-items-start mb-4">
          <Body size="small">Device model</Body>
          <Select
            className={
              formData?.step2?.errors?.deviceType
                ? "shipment-form-fields shipment-form-field-error"
                : "shipment-form-fields"
            }
            style={{
              marginTop: "8px",
            }}
            placeholder="Select device type"
            value={formData?.step2?.deviceType}
            options={[
              { value: "", label: "Select device type" },
              ...(Array.isArray(formData?.step2?.deviceTypes)
                ? formData?.step2?.deviceTypes
                : []
              ).map((type) => ({
                label: type.devicetype,
                value: type.devicetype,
              })),
            ]}
            onChange={(value) => {
              dispatch(fetchDeviceProfilesRequest(value));
              handleFieldChange("deviceType", value);
            }}
          />
          {formData?.step2?.errors?.deviceType && (
            <div style={{ paddingTop: 5 }}>
              <ErrorMessage
                fieldName="devicetype"
                errors={{ devicetype: "Device type is required" }}
              />
            </div>
          )}
        </div>
        <div className="d-flex flex-column justify-content-start align-items-start mb-4">
          <Body size="small">Device ID</Body>
          <Select
            className={
              formData?.step2?.errors?.deviceId
                ? "shipment-form-fields shipment-form-field-error"
                : "shipment-form-fields"
            }
            style={{
              marginTop: "8px",
            }}
            placeholder="Select device Name"
            value={formData?.step2?.deviceId?.id || ""}
            options={[
              { value: "", label: "Select device name" },
              ...(formData?.step2?.deviceType &&
              Array.isArray(formData?.step2?.deviceNames)
                ? formData?.step2?.deviceNames
                    .filter((dname) => {
                      return dname.devicetype === formData?.step2?.deviceType;
                    })
                    .map((dname) => ({
                      label: dname.name || dname.serial,
                      value: dname.uid,
                      bleserial: dname.serial,
                    }))
                : []),
            ]}
            onChange={(_, option) => {
              handleFieldChange(
                "deviceId",
                {
                  id: option.value,
                  name: option.label,
                },
                option.bleserial
              );
            }}
          />
          {formData?.step2?.errors?.deviceId && (
            <div style={{ paddingTop: 5 }}>
              <ErrorMessage
                fieldName="deviceId"
                errors={{ deviceId: "Device ID is required" }}
              />
            </div>
          )}
        </div>
        {!formData?.step2?.deviceType && (
          <div className="asset-info-btn">
            <Micro color="#000000">
              Select a device type to show profiles.
            </Micro>
          </div>
        )}
        {formData?.step2?.deviceType && (
          <div className="d-flex flex-column justify-content-center align-items-start w-100 mt-4">
            <Title size="small" bold={true}>
              Select device profile
            </Title>
            {formData?.step2?.errors?.deviceProfile && (
              <div style={{ paddingTop: 5 }}>
                <ErrorMessage
                  fieldName="deviceProfile"
                  errors={{ deviceProfile: "Device Profile is required" }}
                />
              </div>
            )}
            <Table
              loading={isLoadingDeviceProfile}
              className="w-100 asset-info-table"
              columns={[
                {
                  title: (
                    <Body size="large" bold={true}>
                      Profile name
                    </Body>
                  ),
                  dataIndex: "name",
                  key: "name",
                  render: (text, record) => {
                    return (
                      <div
                        style={{
                          marginLeft: "8px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        <RadioButtonGroup
                          onChange={(e) => {
                            const { value } = e.target;
                            handleFieldChange("deviceProfile", {
                              name: text,
                              id: value,
                            });
                          }}
                          error={false}
                          key={record?.key}
                          value={
                            formData?.step2?.deviceProfile?.id || "undefined"
                          } // Ensure value is defined
                          data={[
                            {
                              name: "profile-selection", // same name to group radios
                              label: "",
                              children: text,
                              value: record?.key,
                              ariaLabel: `Select ${record?.name}`,
                              disabled: false,
                            },
                          ]}
                        />
                      </div>
                    );
                  },
                },
                {
                  title: (
                    <Body size="large" bold={true}>
                      Updated
                    </Body>
                  ),
                  dataIndex: "updated",
                  key: "updated",
                  render: (text) => <span>{dayjs(text).fromNow()}</span>,
                },
                {
                  title: (
                    <Body size="large" bold={true}>
                      Created
                    </Body>
                  ),
                  dataIndex: "created",
                  key: "created",
                  render: (text) => (
                    <span>
                      {dayjs(text).format("MM-DD-YY [at] hh:mm:ss a")}
                    </span>
                  ),
                },
                {
                  title: (
                    <Body size="large" bold={true}>
                      Created by
                    </Body>
                  ),
                  dataIndex: "createdBy",
                  key: "createdBy",
                },
              ]}
              dataSource={(Array.isArray(formData?.step2?.deviceProfiles)
                ? formData?.step2?.deviceProfiles
                : []
              )?.map((profile) => ({
                key: profile.profileid,
                name: profile.profilename,
                updated: profile.updatedon,
                created: profile.createdon,
                createdBy: profile.createdby,
              }))}
              pagination={false}
              scroll={{ y: 300 }}
            />
          </div>
        )}
        {formData?.step2?.deviceProfile && (
          <>
            <div className="mt-4">
              <Title size="small" bold={true}>
                {formData?.step2?.deviceProfile?.name}
              </Title>
            </div>
            <div className="d-flex flex-column justify-content-center align-items-start w-100 mt-4">
              <Title size="small" bold={true} color="#6F7171">
                Attributes
              </Title>
              <Table
                className="w-100 asset-info-table"
                columns={[
                  {
                    title: (
                      <Body size="large" bold={true}>
                        Attribute
                      </Body>
                    ),
                    dataIndex: "attribute",
                    key: "attribute",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Min
                        </Body>
                        <ATSToolTip text="Minimum value">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "minimum",
                    key: "minimum",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Max
                        </Body>
                        <ATSToolTip text="Maximum value">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "maximum",
                    key: "maximum",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Alert Type
                        </Body>
                        <ATSToolTip text="Severity level of the alarm">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "alertType",
                    key: "alertType",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Alert Group
                        </Body>
                        <ATSToolTip text="Alert groups to be notified of the alarm">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "alertGroup",
                    key: "alertGroup",
                    render: (text) => {
                      const alertGroup = text?.split("_") || []
                      return (<span>{alertGroup?.length > 1 ? alertGroup[alertGroup?.length-1] : "N/A"}</span>)
                    },
                  },
                ]}
                dataSource={formData?.step2?.attributes || []}
                pagination={false}
                scroll={{ y: 300 }}
              />
            </div>
            <div className="d-flex flex-column justify-content-center align-items-start w-100 mt-4">
              <Title size="small" bold={true} color="#6F7171">
                Location
              </Title>
              <Table
                className="w-100 asset-info-table"
                columns={[
                  {
                    title: (
                      <Body size="large" bold={true}>
                        Zone
                      </Body>
                    ),
                    dataIndex: "zone",
                    key: "zone",
                    render: (zone) => {
                      return (
                        <div>
                          {zone?.label ? (
                            <div>{zone?.label}</div>
                          ) : (
                            zone?.value || "N/A"
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Trigger
                        </Body>
                        <ATSToolTip text="Trigger alerts on">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "trigger",
                    key: "trigger",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Alert Type
                        </Body>
                        <ATSToolTip text="Severity level of the alarm">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "alerttype",
                    key: "alerttype",
                  },
                  {
                    title: (
                      <div className="d-flex flex-row align-items-center">
                        <Body size="large" bold={true}>
                          Alert Group
                        </Body>
                        <ATSToolTip text="Alert groups to be notified of the alarm">
                          <img
                            src={CircleIIcon}
                            className="mx-1"
                            alt="info"
                            style={{ verticalAlign: "middle" }}
                          />
                        </ATSToolTip>
                      </div>
                    ),
                    dataIndex: "alertgroup",
                    key: "alertGroup",
                    render: (text) => {
                      const alertGroup = text?.split("_") || []
                      return (<span>{alertGroup?.length > 1 ? alertGroup[alertGroup?.length-1] : "N/A"}</span>)
                    },
                  },
                ]}
                dataSource={formData?.step2?.locations || []}
                pagination={false}
                scroll={{ y: 300 }}
              />
            </div>
            <div className="d-flex flex-column justify-content-center align-items-start w-100 mt-4">
              <Title size="small" bold={true} color="#6F7171">
                Frequency
              </Title>
              <div className="w-100 d-flex justify-content-start align-items-center pt-2">
                <div className="w-33 d-flex flex-column justify-content-start align-items-start">
                  <Body size="large" bold={true}>
                    Reporting Frequency
                  </Body>
                  <Body size="large" bold={false}>
                    {formData?.step2?.frequencies?.capturesensorvalues || "N/A"}
                  </Body>
                </div>

                <div className="w-33 d-flex flex-column justify-content-start align-items-start">
                  <Body size="large" bold={true}>
                    Monitoring Frequency
                  </Body>
                  <Body size="large" bold={false}>
                    {formData?.step2?.frequencies?.sensordatafrequency || "N/A"}
                  </Body>
                </div>
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter
        buttonData={{
          close: {
            width: "100%",
            children: "Next",
            onClick: handleNext,
          },
          primary: {
            children: "Back",
            width: "100%",
            onClick: onExit,
          },
        }}
      />
    </>
  );
}

CreateAssetStep2.propTypes = {
  formData: PropTypes.object.isRequired,
  onExit: PropTypes.func.isRequired,
};

export default CreateAssetStep2;
